import { TEAM_MANAGED_BACKLOG } from '@atlassian/jira-capabilities/src/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { AsyncHorizontalNavJira } from '@atlassian/jira-horizontal-nav/src/async.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@atlassian/jira-issue-preload-layout/src/index.tsx';
import { LazyIssueApp } from '@atlassian/jira-lazy-issue-app/src/LazyIssueApp.tsx';
import { MENU_ID, SIDEBAR_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import {
	forgeBacklogModuleResource,
	forgeSprintModuleResource,
} from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import AsyncSidebarNextGen from '@atlassian/jira-navigation-apps-sidebar-next-gen/src/async.tsx';
import { createEntry } from '@atlassian/jira-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@atlassian/jira-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { backlogResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/backlog/index.tsx';
import { backlogFilterRefinementResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/filter-refinement/index.tsx';

import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { incidentExperimentTraitResourceOnNav3Only } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';
import { softwareBacklogRoute } from '@atlassian/jira-router-routes-software-backlog-routes/src/softwareBacklogRoute.tsx';
import { NextGenBacklogSkeleton } from '@atlassian/jira-skeletons/src/ui/next-gen-backlog/NextGenBacklogSkeleton.tsx';
import { softwareProjectLayout } from '@atlassian/jira-software-project-layout/src/index.tsx';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils/src/utils/resource-with-condition/index.tsx';
import { projectThemeResource } from '@atlassian/jira-router-resources-project-theme/src/index.tsx';
import { getIsSoftwareThemingEnabled } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { aiEntitlementResource } from '@atlassian/jira-router-resources-ai-entitlement/src/services/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { customFiltersResource } from '@atlassian/jira-router-resources-next-gen-backlog/src/services/custom-filters/index.tsx';
import { backlogPageEntrypoint } from './ui/index.tsx';

export const softwareBacklogRouteEntry = createEntry(softwareBacklogRoute, () => ({
	group: 'software',
	skeleton: NextGenBacklogSkeleton,

	layout: composeLayouts(
		softwareProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{
				query: ['selectedIssue'],
			},
		]),
	),

	navigation: {
		menuId: MENU_ID.PROJECTS,
		sidebar: AsyncSidebarNextGen,
		horizontal: AsyncHorizontalNavJira,
		onlyShowHorizontalOnNav4: true,
		sidebarId: fg('jfp-magma-fix-nav4-sidebar-client-api-call') ? SIDEBAR_ID.CONTENT : undefined,
	},

	resources: [
		...getNavigationResources({
			isUsingSidebarEntrypoint: fg('jfp-magma-fix-nav4-sidebar-client-api-call'),
		}),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
		incidentExperimentTraitResourceOnNav3Only,
		projectContextResource,
		staticSidebarShortcutsResource,
		staticProjectSidebarNavigationResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		backlogResource,
		resourceWithCondition2(
			() => !expVal('filter_refinement_in_tmp_backlog', 'isEnabled', false),
			customFiltersResource,
		),
		backlogFilterRefinementResource,
		forgeBacklogModuleResource,
		forgeSprintModuleResource,
		resourceWithCondition2(() => getIsSoftwareThemingEnabled(), projectThemeResource),
		aiEntitlementResource,
	],

	preloadOptions: {
		earlyChunks: ['software-backlog-early-entry'],
	},

	afterPaint: [LazyIssueApp],
	ufoName: 'next-gen-backlog',

	meta: {
		capability: TEAM_MANAGED_BACKLOG,
	},

	entryPoint: () => backlogPageEntrypoint,
	supportsPreviewPanel: true,
	isHydrateRootEnabled: fg('tmp-backlog-hydrate-root'),

	bifrost: {
		bifrostGroup: 'shell-only',

		bundling: {
			packageName:
				'@atlassian/jira-router-routes-software-backlog-entries/src/softwareBacklogRouteEntry.tsx',
			whichExportToUse: 'softwareBacklogRouteEntry',
		},

		enabled: true,
	},
}));
